import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Download, Upload, Archive } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BackupDialog = ({ open, onOpenChange, onImported }) => {
  const [busyExport, setBusyExport] = React.useState(false);
  const [busyImport, setBusyImport] = React.useState(false);
  const fileRef = React.useRef(null);

  const exportZip = async () => {
    setBusyExport(true);
    const toastId = toast.loading("Création de la sauvegarde…", {
      description: "Construction du ZIP côté serveur, ne ferme pas l'app.",
    });
    try {
      const token = typeof window !== "undefined"
        ? window.localStorage.getItem("gl26_auth_token")
        : null;
      if (!token) {
        toast.dismiss(toastId);
        toast.error("Non authentifié — recharge la page");
        return;
      }

      // Use fetch + Blob: most reliable across desktop, iOS Safari, iOS PWA,
      // Android Chrome, Samsung Internet, Brave, Firefox.
      // `cache: "no-store"` guarantees the service worker cannot intercept.
      const res = await fetch(`${API}/backup/export`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        cache: "no-store",
      });

      if (!res.ok) {
        toast.dismiss(toastId);
        toast.error(`Échec de l'export (HTTP ${res.status})`);
        return;
      }

      // Filename from Content-Disposition (or fallback)
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, "");
      const filename = match?.[1] || `giglive26-backup-${ts}.zip`;

      // Stream the response body into a Blob.
      // Reading via .blob() works on every browser including iOS Safari.
      const blob = await res.blob();

      // Trigger the actual download
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.rel = "noopener";
      // Required for some Android browsers (Samsung Internet, MIUI) to actually
      // honor the download attribute on a programmatic click.
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Slight delay before revoking so all browsers finish reading the URL
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);

      const sizeMb = (blob.size / 1024 / 1024).toFixed(blob.size > 10 * 1024 * 1024 ? 0 : 2);
      toast.dismiss(toastId);
      toast.success(`Sauvegarde téléchargée · ${sizeMb} Mo`);
    } catch (e) {
      toast.dismiss(toastId);
      // Common cases: network drop, server timeout, CORS
      const msg = e?.message ? `Échec de l'export : ${e.message}` : "Échec de l'export";
      toast.error(msg);
    } finally {
      setBusyExport(false);
    }
  };

  const importZip = async (f) => {
    setBusyImport(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/backup/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Restauré : ${data.songs} morceaux, ${data.files} fichiers`);
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Échec de l'import");
    } finally {
      setBusyImport(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="backup-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-[var(--gl-gold)] font-display flex items-center gap-2">
            <Archive size={20} /> Sauvegarde & Restauration
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Tout exporter dans un fichier ZIP (morceaux, setlists, MP3/WAV, PDF, enregistrements live et galerie photos/vidéos) — et restaurer en un clic.
          </DialogDescription>
        </DialogHeader>

        <button
          onClick={exportZip}
          disabled={busyExport}
          className="w-full tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-50"
          data-testid="backup-export-btn"
        >
          <Download size={18} />
          {busyExport ? "Création de la sauvegarde…" : "Télécharger une sauvegarde ZIP"}
        </button>

        <div className="text-center text-[10px] uppercase tracking-widest text-neutral-500 my-1">— ou —</div>

        <button
          onClick={() => fileRef.current?.click()}
          disabled={busyImport}
          className="w-full tap-lg rounded-xl bg-[#121212] border-2 border-dashed border-[var(--gl-neon)]/40 hover:border-[var(--gl-neon)] text-[var(--gl-neon)] font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="backup-import-btn"
        >
          <Upload size={18} />
          {busyImport ? "Restauration en cours…" : "Restaurer depuis un ZIP"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) importZip(f);
            e.target.value = "";
          }}
        />
        <p className="text-[11px] text-neutral-500 leading-snug pt-1">
          La restauration met à jour les morceaux existants et ajoute les nouveaux. Aucune perte de données.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default BackupDialog;
